import { useRef, useCallback, useEffect, useMemo } from 'react';
import { useSearchCardsQuery, useGetCardPrintingsQuery } from 'store/services/cards/search/cardsSearchApi';
import { useWindowSize } from 'hooks/useWindowSize';
import { useCardHoverPreview } from 'hooks/useCardHoverPreview';
import CardImageTooltip from './CardImageTooltip';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { selectDisplayPrinting } from 'utils/displayPrinting';
import './CardLink.css';

interface CardLinkProps {
  cardName: string;
  children?: React.ReactNode;
}

// The /cards/search-light and /cards/:id/printings payloads are lighter than the
// declared MTGCard type and differ from it: both use lowercase `scryfallId`, and
// the printings payload omits `name` and `digital`. Type the fields we actually
// read off them accurately so access matches the real runtime shape.
interface ResolvedCard {
  id?: number;
  name?: string;
  scryfallId?: string;
  setCode?: string;
  collectorNumber?: string | null;
  lang?: string;
  releasedAt?: string;
  borderColor?: string;
  digital?: boolean;
  imageUris?: { normal?: string } | null;
  normal_fallbacks?: string[];
}

// Module-level cache for card data to avoid redundant API calls
const cardDataCache = new Map<string, { scryfallId?: string; name: string; setCode?: string; collectorNumber?: string | null; imageUrl?: string; normalFallbacks?: string[] }>();

const MOBILE_BREAKPOINT = 768;

const CardLink = ({ cardName, children }: CardLinkProps) => {
  const linkRef = useRef<HTMLAnchorElement>(null);

  const { isMobile } = useWindowSize(MOBILE_BREAKPOINT);
  // Show/hide timing lives in the shared hook (also used by the Finalize Trade modal).
  const preview = useCardHoverPreview({ disabled: isMobile });
  const { isHovered, showTooltip, position: tooltipPosition } = preview;

  // Check cache first before making API call
  const cachedData = cardDataCache.get(cardName.toLowerCase());

  // Only fetch when hovering and not in cache
  const shouldFetch = isHovered && !cachedData;

  const { data, isLoading, isError } = useSearchCardsQuery(
    { name: cardName, exact: true, limit: 1 },
    { skip: !shouldFetch }
  );

  // Resolve the card by name first (gives us its id), then fetch all printings so
  // we can show the original / most-recognizable printing instead of whatever
  // printing the name search happened to return first.
  const searchCard = data?.cards?.[0] as ResolvedCard | undefined;
  const {
    data: rawPrintings,
    isLoading: printingsLoading,
    isError: printingsError,
  } = useGetCardPrintingsQuery(
    { cardId: searchCard?.id as number },
    { skip: !shouldFetch || !searchCard?.id }
  );
  const printings = rawPrintings as ResolvedCard[] | undefined;

  // Pick the representative printing via the shared comparator (English-first,
  // oldest-first, standard-frame guard). Fall back to the raw search hit if the
  // printings list is unavailable.
  const chosen = useMemo(
    () => selectDisplayPrinting(printings) ?? searchCard,
    [printings, searchCard]
  );

  // The printings payload omits `name`, so fall back to the search hit / prop.
  const resolvedName = chosen?.name ?? searchCard?.name ?? cardName;

  // Get card data from cache or the chosen printing.
  const scryfallId = cachedData?.scryfallId || chosen?.scryfallId;
  const imageUrl = cachedData?.imageUrl || chosen?.imageUris?.normal;
  const normalFallbacks = cachedData?.normalFallbacks || chosen?.normal_fallbacks;

  // Cache the chosen printing once selection has settled (printings loaded or
  // errored) so we store the original printing — not the interim search hit —
  // and avoid refetching on the next hover.
  useEffect(() => {
    if (cachedData || !chosen) return;
    const selectionSettled = !!printings || printingsError || !searchCard?.id;
    if (!selectionSettled) return;
    cardDataCache.set(cardName.toLowerCase(), {
      scryfallId: chosen.scryfallId,
      name: resolvedName,
      setCode: chosen.setCode,
      collectorNumber: chosen.collectorNumber,
      imageUrl: chosen.imageUris?.normal,
      normalFallbacks: chosen.normal_fallbacks,
    });
  }, [chosen, resolvedName, printings, printingsError, searchCard?.id, cachedData, cardName]);

  // Build the best URL from cached/fetched data
  const cardUrl = useMemo(() => {
    const urlData = cachedData || (chosen ? { scryfallId: chosen.scryfallId, name: resolvedName, setCode: chosen.setCode, collectorNumber: chosen.collectorNumber } : null);
    if (urlData) {
      return buildCardUrl({ name: urlData.name, setCode: urlData.setCode, collectorNumber: urlData.collectorNumber, scryfallId: urlData.scryfallId });
    }
    return `/search/results?name=${encodeURIComponent(cardName)}&exact=true`;
  }, [cachedData, chosen, resolvedName, cardName]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      window.open(cardUrl, '_blank', 'noopener,noreferrer');
    },
    [cardUrl]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick(e as unknown as React.MouseEvent);
      }
    },
    [handleClick]
  );

  // Keyboard focus has no cursor, so the preview anchors under the link.
  const handleFocus = useCallback(() => {
    if (!linkRef.current) return;
    const rect = linkRef.current.getBoundingClientRect();
    preview.showAt({ x: rect.left, y: rect.bottom + 5 });
  }, [preview]);

  const handleBlur = preview.hideSoon;

  // Determine loading state. Keep the tooltip loading until printing selection
  // settles so we never flash the interim search printing before swapping to the
  // original.
  const printingsPending = !!searchCard?.id && !cachedData && printingsLoading;
  const tooltipIsLoading =
    isLoading || printingsPending || (isHovered && !cachedData && !chosen && !isError);

  return (
    <>
      <a
        ref={linkRef}
        className="card-link"
        href={cardUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`View ${cardName} card details`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...preview.triggerHandlers}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {children || cardName}
      </a>
      {showTooltip && !isMobile && (
        <CardImageTooltip
          scryfallId={scryfallId}
          imageUrl={imageUrl}
          normalFallbacks={normalFallbacks}
          cardName={cardName}
          position={tooltipPosition}
          isLoading={tooltipIsLoading}
          {...preview.tooltipHandlers}
        />
      )}
    </>
  );
};

export default CardLink;
